import * as THREE from '../../libs/three.js/build/three.module.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { Utils } from '../utils.js';
import { getMousePointCloudIntersection } from './SharedUtils.js';
import { packOccupancyHash, OCC_TEX_WIDTH } from './occupancyHash.js';
import { TextSprite } from '../TextSprite.js';
import { decodeHeightRaster, profileVolume } from './surfaceVolume.js';
import { effectiveProfileRangeM, profileNormal } from './surfaceProfile.js';

/** Same mesh as Point / Distance markers (`Measure.sphereGeometry`). */
export const MEASURE_MARKER_GEOMETRY = new THREE.SphereGeometry(0.4, 10, 10);
/** Cloud snap colour used by measurement markers. */
export const MEASURE_CLOUD_MARKER_COLOR = 0xef4444;

export function createMeasureMarkerMaterial(color = MEASURE_CLOUD_MARKER_COLOR) {
  return new THREE.MeshLambertMaterial({
    color,
    emissive: 0x000000,
  });
}

const _markerSize = new THREE.Vector2();
const _markerWorld = new THREE.Vector3();

/** Constant on-screen size: scale `sphere` (or a label) to `px` pixels per unit. */
export function scaleMeasureMarker(sphere, camera, renderer, px = 15) {
  if (!sphere || !camera || !renderer) return;
  const size = renderer.getSize(_markerSize);
  const distance = camera.position.distanceTo(sphere.getWorldPosition(_markerWorld));
  const pr = Utils.projectedRadius(1, camera, distance, size.width, size.height);
  const scale = px / Math.max(pr, 1e-6);
  sphere.scale.set(scale, scale, scale);
  return scale;
}

export const SURFACE_GPU_CAP = 8;

/** Max surfaces stored and restored in a Noach view (≤ GPU slot count). */
export const MAX_SURFACES_PER_VIEW = 5;

export function canAddSurface(scene) {
  return (scene?.surfaces?.length ?? 0) < MAX_SURFACES_PER_VIEW;
}

export function surfaceLimitMessage() {
  const fallback = `A view can have at most ${MAX_SURFACES_PER_VIEW} surfaces. Remove one to add another.`;
  if (typeof i18n === 'undefined' || !i18n.t) return fallback;
  return i18n.t('surfaces.limit', {
    max: MAX_SURFACES_PER_VIEW,
    defaultValue: fallback,
  });
}

/**
 * Visible point clouds the grow endpoint cannot serve: no backend cloud id
 * (legacy sites) or no occupancy sidecar (`noachSurfaceReady` is stamped by
 * the host from the resources catalog; anything but `true` counts as missing).
 * Hidden clouds are ignored so the user can hide an un-indexed cloud to keep
 * using the tool on the others.
 */
export function surfaceIndexBlockers(scene) {
  return (scene?.pointclouds || []).filter(
    (pc) => pc.visible !== false && (!pc.noachCloudUuid || pc.noachSurfaceReady !== true)
  );
}

export function surfaceIndexMissingMessage(blockers) {
  const name = blockers?.[0]?.name || '';
  const fallback = `Surface detection is unavailable: "${name}" has no surface index yet.`;
  if (typeof i18n === 'undefined' || !i18n.t) return fallback;
  return i18n.t('surfaces.index_missing', { name, defaultValue: fallback });
}

/** Relative anticipated error for display, e.g. `"5.9%"`. Empty if not meaningful. */
export function anticipatedErrorPercent(error, total) {
  if (!Number.isFinite(error) || !(total > 0)) return '';
  return `${((100 * error) / total).toFixed(1)}%`;
}

/** Slider / saved-view cap for extra plaster width (metres). UI shows centimetres. */
export const EXTRA_PLASTER_MAX_M = 0.25;

export function clampExtraPlasterM(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(EXTRA_PLASTER_MAX_M, n);
}

/** "Distance from plane" slider cap (metres); members lie within ±this of the pick plane. */
export const MAX_DISTANCE_MAX_M = 0.25;
/** Height-profile level offset bound (m): the slab half-width; in ideal mode the deviation
 * is bounded by extent × tan(tilt) instead, so a tilted surface can still saturate. */
export const PROFILE_OFFSET_MAX_M = MAX_DISTANCE_MAX_M;
/**
 * Height-profile range slider bounds (m) — the same for the below and above
 * sliders. 0 is a real value ("virtually nothing on this side": the shader and
 * the volume floor it at 0.1 mm), distinct from null = follow the grown slab.
 */
export const PROFILE_RANGE_MIN_M = 0;
export const PROFILE_RANGE_MAX_M = MAX_DISTANCE_MAX_M;

export function clampProfileOffsetM(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(PROFILE_OFFSET_MAX_M, Math.max(-PROFILE_OFFSET_MAX_M, n));
}

/** `null` means "follow the grown maxDistance" (the default until the user pins a value); 0 is pinned zero. */
export function clampProfileRangeM(value) {
  // Number(null) is 0, which is now a real value: missing stays "follow".
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(PROFILE_RANGE_MAX_M, Math.max(PROFILE_RANGE_MIN_M, n));
}

/**
 * Plaster volume including optional extra width: V = V0 + S × w, where V0 is
 * the fill over the highlighted cells (see `surfaceVolume.js`) and S their
 * area. `extraPlasterM` is metres (UI shows cm); default 0 leaves V0
 * unchanged. `null` until the first profile computation has finished.
 */
export function plasterVolumeM3(surface) {
  const v0 = surface?.volumeM3;
  if (!Number.isFinite(v0)) return null;
  const s = surface.profileAreaM2;
  const w = surface.extraPlasterM;
  const add = Number.isFinite(s) && Number.isFinite(w) && w > 0 ? s * w : 0;
  return v0 + add;
}

/** Decimals (3..5) so a non-zero cubic-metre value does not print as all zeros; list and panel share it. */
export function volumeDecimals(m3, min = 3, max = 5) {
  let d = min;
  if (!Number.isFinite(m3) || m3 <= 0) return d;
  while (d < max && Number(m3.toFixed(d)) === 0) d++;
  return d;
}

/** Anticipated error for {@link plasterVolumeM3}: δV ≈ δV0 + δS × w. */
export function plasterVolumeErrorM3(surface) {
  const e0 = surface?.volumeErrorM3;
  if (!Number.isFinite(e0)) return null;
  const ae = surface.areaErrorM2;
  const w = surface.extraPlasterM;
  const add = Number.isFinite(ae) && Number.isFinite(w) && w > 0 ? ae * w : 0;
  return e0 + add;
}

export const SURFACE_PALETTE = [
  0xe74c3c, 0x3498db, 0x2ecc71, 0xf1c40f, 0x9b59b6, 0xe67e22, 0x1abc9c, 0xe84393,
];

let surfaceCounter = 0;

const _spinZ = new THREE.Vector3(0, 0, 1);
const _spinDir = new THREE.Vector3();
const _worldN = new THREE.Vector3();
const _spinCam = new THREE.Vector3();
const _extentOffset = new THREE.Vector3();
const _extentPos = new THREE.Vector3();

/** Signed centimetres for the extreme-point labels: "+1.2 cm" / "−3.4 cm" (never "−0.0"). */
export function formatExtentCm(metres) {
  const cm = metres * 100;
  const abs = Math.abs(cm).toFixed(1);
  // Sign the rounded magnitude, so −0.05 cm (which rounds to 0.1) is "−0.1", not "+0.1".
  const sign = cm < 0 && Number(abs) !== 0 ? '−' : '+';
  return `${sign}${abs} cm`;
}

function createExtentLabel() {
  const label = new TextSprite('');
  label.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
  label.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
  label.fontsize = 16;
  label.material.depthTest = false;
  label.material.opacity = 1;
  return label;
}

/** Open torus so rotation reads as a spinner, not a solid ring. */
const SURFACE_SPINNER_GEO = new THREE.TorusGeometry(1, 0.16, 8, 40, 0, Math.PI * 1.55);

function emptyOccTexture() {
  const data = new Uint8Array([0, 0, 0, 0]);
  const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

/** Potree 2.0 `loader.offset` is a `[x,y,z]` array; `pcoGeometry.offset` is a Vector3. */
export function octreeOffsetToVector3(offset) {
  if (!offset) return null;
  if (offset.isVector3) return offset.clone();
  if (Array.isArray(offset) && offset.length >= 3 && Number.isFinite(Number(offset[0]))) {
    return new THREE.Vector3(Number(offset[0]), Number(offset[1]), Number(offset[2]));
  }
  if (typeof offset.x === 'number' && Number.isFinite(offset.x)) {
    return new THREE.Vector3(offset.x, offset.y, offset.z);
  }
  return null;
}

export function scenePointToOffsetFrame(pointcloud, scenePoint) {
  const inv = new THREE.Matrix4().copy(pointcloud.matrixWorld).invert();
  const local = scenePoint.clone().applyMatrix4(inv);
  const geomOffset = octreeOffsetToVector3(pointcloud.pcoGeometry?.offset);
  const metaOffset = octreeOffsetToVector3(pointcloud.pcoGeometry?.loader?.offset) ?? geomOffset;
  const out = local.clone();
  if (geomOffset) out.add(geomOffset);
  if (metaOffset) out.sub(metaOffset);
  return out;
}

const _offGeom = new THREE.Vector3();
const _offMeta = new THREE.Vector3();
const _offLocal = new THREE.Vector3();

function readOffsetInto(offset, target) {
  if (!offset) return null;
  if (offset.isVector3) {
    target.copy(offset);
    return target;
  }
  if (Array.isArray(offset) && offset.length >= 3 && Number.isFinite(Number(offset[0]))) {
    target.set(Number(offset[0]), Number(offset[1]), Number(offset[2]));
    return target;
  }
  if (typeof offset.x === 'number' && Number.isFinite(offset.x)) {
    target.set(offset.x, offset.y, offset.z);
    return target;
  }
  return null;
}

export function offsetFrameToScene(pointcloud, offsetPoint) {
  const geomOffset = readOffsetInto(pointcloud.pcoGeometry?.offset, _offGeom);
  const metaOffset =
    readOffsetInto(pointcloud.pcoGeometry?.loader?.offset, _offMeta) ?? geomOffset;
  _offLocal.copy(offsetPoint);
  if (metaOffset) _offLocal.add(metaOffset);
  if (geomOffset) _offLocal.sub(geomOffset);
  return _offLocal.applyMatrix4(pointcloud.matrixWorld);
}

function textureFromPackedBytes(data, width, height, extras) {
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.needsUpdate = true;
  return {
    texture,
    height,
    count: extras.count,
    blockCount: extras.blockCount,
    frontierCount: extras.frontierCount,
    memberHashSize: extras.memberHashSize,
    shellHashSize: extras.shellHashSize,
    frontierHashSize: extras.frontierHashSize,
    snapHashSize: extras.snapHashSize,
    aabbMin: extras.aabbMin,
    aabbMax: extras.aabbMax,
  };
}

function emptyPackedTexture() {
  return {
    texture: emptyOccTexture(),
    height: 1,
    count: 0,
    blockCount: 0,
    frontierCount: 0,
    memberHashSize: 0,
    shellHashSize: 0,
    frontierHashSize: 0,
    snapHashSize: 0,
    aabbMin: [0, 0, 0],
    aabbMax: [0, 0, 0],
  };
}

export function occupancyTextureFromPacked(
  base64,
  memberCount,
  blockCount = 0,
  frontierCount = 0,
  planeNormal = null,
  snapCount = 0
) {
  const nMem = Math.max(0, memberCount | 0);
  const nBlk = Math.max(0, blockCount | 0);
  const nFront = Math.max(0, frontierCount | 0);
  if (!base64 || nMem + nBlk + nFront <= 0) {
    return emptyPackedTexture();
  }
  const binary = atob(base64);
  const raw = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) raw[i] = binary.charCodeAt(i);
  const hashed = packOccupancyHash(raw, nMem, nBlk, nFront, planeNormal, snapCount);
  const width = hashed.width || OCC_TEX_WIDTH;
  const height = hashed.height;
  const data = new Uint8Array(width * height * 4);
  data.set(hashed.data.subarray(0, Math.min(hashed.data.length, data.length)));
  return textureFromPackedBytes(data, width, height, {
    count: nMem,
    blockCount: nBlk,
    frontierCount: nFront,
    memberHashSize: hashed.memberHashSize,
    shellHashSize: hashed.shellHashSize,
    frontierHashSize: hashed.frontierHashSize,
    snapHashSize: hashed.snapHashSize,
    aabbMin: hashed.aabbMin,
    aabbMax: hashed.aabbMax,
  });
}

let _occWorker = null;
/** Set once the worker failed to load or crashed; later calls go straight to the fallback. */
let _occWorkerFailed = false;
let _occReqId = 0;
let _occFallbackLogged = false;
const _occPending = new Map();

/** Main-thread pack above this voxel count freezes the camera; skip it in dev. */
export const OCC_MAIN_THREAD_FALLBACK_MAX = 20_000;

function occupancyWorkerUrl() {
  if (typeof Potree === 'undefined' || !Potree.scriptPath) return null;
  return `${Potree.scriptPath}/workers/OccupancyHashWorker.js`;
}

function isOccupancyDevHost() {
  if (typeof Potree !== 'undefined' && Potree.debug?.occupancyWorkerRequired) return true;
  if (typeof location === 'undefined' || !location) return false;
  const host = location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

function logOccupancyFallbackOnce(reason) {
  if (_occFallbackLogged) return;
  _occFallbackLogged = true;
  const detail = reason ? ` (${reason})` : '';
  console.error(
    `OccupancyHashWorker unavailable${detail}; packing occupancy on the main thread.`
  );
}

function occupancyFallback(base64, nMem, nBlk, nFront, planeNormal, reason, snapCount = 0) {
  const count = nMem + nBlk + nFront;
  const large = count > OCC_MAIN_THREAD_FALLBACK_MAX;
  if (isOccupancyDevHost() && large) {
    const err = new Error(
      reason || 'OccupancyHashWorker.js missing; refusing main-thread pack of a large hash'
    );
    console.error(err.message);
    return Promise.reject(err);
  }
  logOccupancyFallbackOnce(reason);
  return Promise.resolve(
    occupancyTextureFromPacked(base64, nMem, nBlk, nFront, planeNormal, snapCount)
  );
}

function ensureOccupancyWorker() {
  if (_occWorker) return _occWorker;
  if (_occWorkerFailed || typeof Worker === 'undefined') return null;
  const url = occupancyWorkerUrl();
  if (!url) return null;
  try {
    const worker = new Worker(url);
    worker.onmessage = (ev) => {
      const msg = ev.data || {};
      const pending = _occPending.get(msg.id);
      if (!pending) return;
      _occPending.delete(msg.id);
      if (msg.ok) pending.resolve(msg);
      else pending.reject(new Error(msg.error || 'occupancy pack failed'));
    };
    worker.onerror = (err) => {
      for (const pending of _occPending.values()) {
        pending.reject(err);
      }
      _occPending.clear();
      _occWorker = null;
      _occWorkerFailed = true;
      worker.terminate();
    };
    _occWorker = worker;
    return worker;
  } catch {
    _occWorkerFailed = true;
    return null;
  }
}

function occupancyTextureFromPackedAsync(
  base64,
  memberCount,
  blockCount = 0,
  frontierCount = 0,
  planeNormal = null,
  snapCount = 0
) {
  const nMem = Math.max(0, memberCount | 0);
  const nBlk = Math.max(0, blockCount | 0);
  const nFront = Math.max(0, frontierCount | 0);
  if (!base64 || nMem + nBlk + nFront <= 0) {
    return Promise.resolve(emptyPackedTexture());
  }
  const worker = ensureOccupancyWorker();
  if (!worker) {
    return occupancyFallback(
      base64,
      nMem,
      nBlk,
      nFront,
      planeNormal,
      occupancyWorkerUrl() ? 'worker failed to start' : 'OccupancyHashWorker.js missing',
      snapCount
    );
  }
  const id = ++_occReqId;
  return new Promise((resolve, reject) => {
    _occPending.set(id, {
      resolve: (msg) => {
        const data = new Uint8Array(msg.buffer);
        resolve(
          textureFromPackedBytes(data, msg.width, msg.height, {
            count: nMem,
            blockCount: nBlk,
            frontierCount: nFront,
            memberHashSize: msg.memberHashSize,
            shellHashSize: msg.shellHashSize,
            frontierHashSize: msg.frontierHashSize,
            snapHashSize: msg.snapHashSize,
            aabbMin: msg.aabbMin,
            aabbMax: msg.aabbMax,
          })
        );
      },
      reject,
    });
    worker.postMessage({
      id,
      base64,
      memberCount: nMem,
      blockCount: nBlk,
      frontierCount: nFront,
      planeNormal,
      snapCount,
    });
  }).catch((err) =>
    occupancyFallback(
      base64,
      nMem,
      nBlk,
      nFront,
      planeNormal,
      err?.message || 'worker error',
      snapCount
    )
  );
}

export function surfaceI18n(key, fallback) {
  if (typeof i18n === 'undefined' || !i18n.t) return fallback;
  return i18n.t(key, fallback);
}

function seedEqualsOffset(offset, xyz) {
  if (!offset || !xyz || xyz.length < 3) return false;
  return (
    Math.abs(offset.x - xyz[0]) <= 1e-7 &&
    Math.abs(offset.y - xyz[1]) <= 1e-7 &&
    Math.abs(offset.z - xyz[2]) <= 1e-7
  );
}

function applyDraftIfUnchanged(surface, key, sent, next) {
  if (!Number.isFinite(next)) return;
  if (sent == null || surface[key] === sent) {
    surface[key] = next;
  }
}

export const SURFACE_VIS = {
  HIGHLIGHT: 'highlight',
  RAINBOW: 'rainbow',
  GROWTH: 'growth',
};

/** Map a saved view record (`visMode` or legacy `rainbow`; unknown/plane → highlight). */
export function normalizeSurfaceVis(data = {}) {
  const mode = data.visMode ?? data.mode;
  if (
    mode === SURFACE_VIS.RAINBOW ||
    mode === SURFACE_VIS.HIGHLIGHT ||
    mode === SURFACE_VIS.GROWTH
  ) {
    return mode;
  }
  if (data.rainbow) return SURFACE_VIS.RAINBOW;
  return SURFACE_VIS.HIGHLIGHT;
}

/** Height-profile reference plane: the fitted plane, or the ideal plumb / level one. */
export const PROFILE_REF = {
  FITTED: 'fitted',
  IDEAL: 'ideal',
};

/** Map a saved view value; anything unknown falls back to the fitted plane (today's look). */
export function normalizeProfileRef(value) {
  return value === PROFILE_REF.IDEAL ? PROFILE_REF.IDEAL : PROFILE_REF.FITTED;
}

/**
 * Plaster top the volume fills up to: the highest highlighted point (nothing
 * is chipped), or the reference plane's zero level (the proud is chipped off
 * first, so only the hollows take plaster).
 */
export const VOLUME_TOP = {
  HIGHEST: 'highest',
  PLANE: 'plane',
};

/** Map a saved view value; anything unknown falls back to the highest point. */
export function normalizeVolumeTop(value) {
  return value === VOLUME_TOP.PLANE ? VOLUME_TOP.PLANE : VOLUME_TOP.HIGHEST;
}

/**
 * Height-profile colour resolution: 0 = continuous gradient, N = N discrete
 * colours (half below, half above the zero level). Uploaded through the
 * `uSurfaceRainbow` uniform (0 off, 1 continuous, N banded) — no new uniform.
 */
export const PROFILE_BANDS = [0, 2, 4, 8];

/** Map a saved view value (number or numeric string); anything unknown is continuous (today's look). */
export function normalizeProfileBands(value) {
  const n = Number(value);
  return PROFILE_BANDS.includes(n) ? n : 0;
}

export function nextSurfaceColor(existing) {
  const used = new Set(
    (existing || []).map((s) => s.color?.getHex?.() ?? s.color)
  );
  for (const hex of SURFACE_PALETTE) {
    if (!used.has(hex)) return new THREE.Color(hex);
  }
  return new THREE.Color(SURFACE_PALETTE[existing.length % SURFACE_PALETTE.length]);
}

/**
 * Plaster volume for the current height profile, off the main thread when
 * the occupancy worker is available (a yard raster is 2 M cells), otherwise
 * synchronously from the same pure function.
 */
function profileVolumeAsync(raster, plane, centroid, params) {
  const worker = ensureOccupancyWorker();
  if (!worker) {
    return Promise.resolve(profileVolumeSafe(raster, plane, centroid, params));
  }
  const id = ++_occReqId;
  return new Promise((resolve, reject) => {
    _occPending.set(id, { resolve: (msg) => resolve(msg.result), reject });
    const { heights, ...rest } = raster;
    // Structured clone copies the heights; the surface keeps its own array.
    worker.postMessage({ kind: 'profileVolume', id, raster: { ...rest, heights }, plane, centroid, params });
  }).catch((err) => {
    logOccupancyFallbackOnce(err?.message || 'worker error');
    return profileVolumeSafe(raster, plane, centroid, params);
  });
}

/** `null` (unknown) instead of a throw, so a bad raster never leaves a stale volume on screen. */
function profileVolumeSafe(raster, plane, centroid, params) {
  try {
    return profileVolume(raster, plane, centroid, params);
  } catch (err) {
    console.error('profileVolume failed', err);
    return null;
  }
}

export class Surface extends EventDispatcher {
  /** Trailing debounce for profile-driven volume recomputes (ms); tests shorten it. */
  static volumeDebounceMs = 100;

  constructor(args = {}) {
    super();
    this.uuid = THREE.MathUtils.generateUUID();
    this.kind = 'surface';
    surfaceCounter += 1;
    this.name = args.name || `Surface ${surfaceCounter}`;
    this.color = args.color || new THREE.Color(SURFACE_PALETTE[0]);
    this.visible = true;
    this.folder = null;
    this.createdAt = Date.now();

    this.pointcloud = args.pointcloud || null;
    this.cloudUuid = args.cloudUuid || null;
    this.scanKey = args.scanKey ?? null;
    this.seedOffset = args.seedOffset || new THREE.Vector3();
    this.angleDeg = 20;
    this.maxDistance = 0.05;
    this.maxGap = 0.08;
    /** Extra plaster thickness (m) added as S×w to volume only; default 0. */
    this.extraPlasterM = 0;
    // Shader highlight follows the last grow, not draft sliders.
    this.grownAngleDeg = this.angleDeg;
    this.grownMaxDistance = this.maxDistance;
    this.flipped = false;
    this.visMode = SURFACE_VIS.HIGHLIGHT;
    /** Height-profile zero-level shift along the flip-aware normal (m). Shader only. */
    this.profileOffsetM = 0;
    /**
     * Height-profile reach below (hollow side, to fill) and above (proud side,
     * to chip off) the zero level (m), each with its own colour scale;
     * null = follow grownMaxDistance. Shader + volume only.
     */
    this.profileRangeBelowM = null;
    this.profileRangeAboveM = null;
    /** Locked (default): the two ranges move together; the panel mirrors one into the other. */
    this.profileRangeLinked = true;
    /** Height-profile reference plane (fitted / ideal plumb-level). Shader only. */
    this.profileRef = PROFILE_REF.FITTED;
    /** Plaster top for the volume: highest highlighted point, or the zero level (chip off the proud). */
    this.volumeTop = VOLUME_TOP.HIGHEST;
    /** Height-profile colour resolution (0 continuous, else 2 / 4 / 8 bands). Shader only. */
    this.profileBands = 0;
    this.plane = {
      origin: [0, 0, 0],
      normal: [0, 0, 1],
    };
    /** Member centroid from grow (offset frame); the height-profile zero level. null = use the pick. */
    this.centroid = null;
    this.ijkOrigin = [0, 0, 0];
    // Last packed snapshot for the shader — plane, centroid + occupancy stay in lockstep.
    this.gpuPlane = this.plane;
    this.gpuCentroid = this.centroid;
    this.gpuIjkOrigin = this.ijkOrigin;
    this.gpuGrownAngleDeg = this.grownAngleDeg;
    this.gpuGrownMaxDistance = this.grownMaxDistance;
    this.gpuCellSize = 0.04;
    this.occupancy3d = null;
    this.occTexture = emptyOccTexture();
    this.occCount = 0;
    this.occBlockCount = 0;
    this.occFrontierCount = 0;
    this.occTexHeight = 1;
    this.memberHashSize = 0;
    this.shellHashSize = 0;
    this.frontierHashSize = 0;
    this.snapHashSize = 0;
    this.aabbMin = [0, 0, 0];
    this.aabbMax = [0, 0, 0];
    this.areaM2 = 0;
    this.areaErrorM2 = 0;
    this.perimeterM = 0;
    this.perimeterErrorM = 0;
    /** Backend per-cell heights (decoded); null until grown. */
    this.heightRaster = null;
    /**
     * Plaster volume for the current height profile, integrated in the
     * viewer from `heightRaster` (see `surfaceVolume.js`); null until the
     * first computation lands after a grow.
     */
    this.volumeM3 = null;
    this.volumeErrorM3 = null;
    /** Area of the highlighted cells the volume integrates over (m²). */
    this.profileAreaM2 = null;
    /** Highest / lowest highlighted cells (offset frame) from the same computation; null when none. */
    this.highlightExtremes = null;
    /** Show those two points on the cloud. Display only; persisted with the view. */
    this.showHighlightExtremes = false;
    this._volumeTimer = null;
    this._volumeEpoch = 0;
    this.busy = false;
    this.detected = false;
    this.hasPlane = false;
    this.error = null;
    this._spin = 0;
    this._occEpoch = 0;

    this.overlay = new THREE.Object3D();
    this.overlay.name = 'surface-overlay';
    this.overlay.visible = false;
    this.seedSphere = new THREE.Mesh(
      MEASURE_MARKER_GEOMETRY,
      createMeasureMarkerMaterial(this.color)
    );
    this.seedSphere.name = 'surface-seed';
    this.seedSphere.surface = this;
    this.arrow = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(),
      1,
      this.color.getHex(),
      0.25,
      0.15
    );
    this.arrow.visible = false;
    this.spinner = new THREE.Mesh(
      SURFACE_SPINNER_GEO,
      new THREE.MeshBasicMaterial({
        color: this.color,
        depthTest: false,
        transparent: true,
        opacity: 0.95,
      })
    );
    this.spinner.name = 'surface-spinner';
    this.spinner.visible = false;
    this.spinner.renderOrder = 10;
    this.overlay.add(this.seedSphere);
    this.overlay.add(this.arrow);
    this.overlay.add(this.spinner);
    // Highest / lowest highlighted cells of the height profile — the top of
    // the plaster and where it is thickest — a thickness line at the lowest,
    // and (lazily, needs a canvas) one label each.
    this.extremes = new THREE.Object3D();
    this.extremes.name = 'surface-highlight-extremes';
    this.extremes.visible = false;
    this.highMarker = new THREE.Mesh(MEASURE_MARKER_GEOMETRY, createMeasureMarkerMaterial(this.color));
    this.highMarker.name = 'surface-high';
    this.lowMarker = new THREE.Mesh(MEASURE_MARKER_GEOMETRY, createMeasureMarkerMaterial(this.color));
    this.lowMarker.name = 'surface-low';
    this.thicknessLine = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
      new THREE.LineBasicMaterial({ color: this.color, depthTest: false, transparent: true, opacity: 0.9 })
    );
    this.thicknessLine.name = 'surface-thickness';
    this.thicknessLine.renderOrder = 10;
    this.highLabel = null;
    this.lowLabel = null;
    this.extremes.add(this.highMarker, this.lowMarker, this.thicknessLine);
    this.overlay.add(this.extremes);
    this._wireDrag();
  }

  _wireDrag() {
    const sphere = this.seedSphere;
    sphere.addEventListener('drag', (e) => {
      const mouse = e.drag?.end || e.viewer?.inputHandler?.mouse;
      if (!mouse || !e.viewer) return;
      const I = getMousePointCloudIntersection(
        mouse,
        e.viewer.scene.getActiveCamera(),
        e.viewer,
        e.viewer.scene.pointclouds,
        { pickClipped: true }
      );
      if (!I?.location) return;
      if (I.pointcloud) this.pointcloud = I.pointcloud;
      if (!this.pointcloud) return;
      sphere.position.copy(I.location);
      this.seedOffset.copy(scenePointToOffsetFrame(this.pointcloud, I.location));
      this._updateArrow();
    });
    sphere.addEventListener('drop', () => {
      this.dispatchEvent({ type: 'seed_moved', surface: this });
    });
    sphere.addEventListener('mouseover', (e) => {
      e.object.material.emissive.setHex(0x555555);
    });
    sphere.addEventListener('mouseleave', (e) => {
      e.object.material.emissive.setHex(0x000000);
    });
    sphere.addEventListener('mouseup', (e) => {
      if (e.button === THREE.MOUSE.RIGHT) {
        this.dispatchEvent({ type: 'select', surface: this });
      }
    });
  }

  /**
   * Apply an HTTP grow result. `request` is the snapshot sent with that call
   * (`seedAtStart`, `paramsAtStart`). Draft slider fields and the seed marker
   * are left alone when the user has changed them since the request started.
   * GPU plane / occupancy update together after packing so the highlight never
   * mixes a new plane with an old hash.
   */
  applyGrowResult(result, viewer, request = {}) {
    if (!result) return Promise.resolve();
    this.plane = result.plane || this.plane;
    // Zero level of the height profile; slider fields (offset / range) are the
    // user's and stay untouched, like extraPlasterM.
    this.centroid = result.centroid ?? null;
    this.ijkOrigin = result.ijkOrigin || [0, 0, 0];
    this.areaM2 = result.areaM2 ?? 0;
    this.areaErrorM2 = result.areaErrorM2 ?? 0;
    this.perimeterM = result.perimeterM ?? 0;
    this.perimeterErrorM = result.perimeterErrorM ?? 0;
    // Volume is integrated here, not by the backend: it depends on the
    // height profile the user is looking at.
    this.heightRaster = decodeHeightRaster(result.heightRaster);
    this.volumeM3 = null;
    this.volumeErrorM3 = null;
    this.profileAreaM2 = null;
    this.highlightExtremes = null;
    this.detected = true;
    const sent = request.paramsAtStart;
    applyDraftIfUnchanged(this, 'angleDeg', sent?.angleDeg, result.angleDeg);
    applyDraftIfUnchanged(this, 'maxDistance', sent?.maxDistance, result.maxDistance);
    applyDraftIfUnchanged(this, 'maxGap', sent?.maxGap, result.maxGap);
    this.grownAngleDeg = Number.isFinite(result.angleDeg) ? result.angleDeg : this.grownAngleDeg;
    this.grownMaxDistance = Number.isFinite(result.maxDistance)
      ? result.maxDistance
      : this.grownMaxDistance;
    this.occupancy3d = result.occupancy3d || null;
    const seedUnmoved =
      !request.seedAtStart || seedEqualsOffset(this.seedOffset, request.seedAtStart);
    if (result.seed && seedUnmoved) {
      this.seedOffset.set(result.seed[0], result.seed[1], result.seed[2]);
    }
    const memberCount = result.occupancy3d?.count ?? 0;
    const blockCount = result.occupancy3d?.blockCount ?? 0;
    const frontierCount = result.occupancy3d?.frontierCount ?? 0;
    const snapCount = result.occupancy3d?.snapCount ?? 0;
    const epoch = ++this._occEpoch;
    this.error = null;
    this.hasPlane = true;
    this._updateOverlay(viewer);
    this.dispatchEvent({ type: 'changed', surface: this });
    this.computeVolume();
    return occupancyTextureFromPackedAsync(
      result.occupancy3d?.data,
      memberCount,
      blockCount,
      frontierCount,
      result.plane?.normal || this.plane?.normal,
      snapCount
    )
      .then((packed) => {
        if (epoch !== this._occEpoch) {
          packed.texture?.dispose?.();
          return;
        }
        this.occTexture?.dispose?.();
        this.occTexture = packed.texture;
        this.occCount = memberCount;
        this.occBlockCount = blockCount;
        this.occFrontierCount = frontierCount;
        this.occTexHeight = packed.height;
        this.memberHashSize = packed.memberHashSize;
        this.shellHashSize = packed.shellHashSize;
        this.frontierHashSize = packed.frontierHashSize;
        this.snapHashSize = packed.snapHashSize;
        this.aabbMin = result.occupancy3d?.aabbMin || packed.aabbMin;
        this.aabbMax = result.occupancy3d?.aabbMax || packed.aabbMax;
        this.gpuPlane = this.plane;
        this.gpuCentroid = this.centroid;
        this.gpuIjkOrigin = this.ijkOrigin;
        this.gpuGrownAngleDeg = this.grownAngleDeg;
        this.gpuGrownMaxDistance = this.grownMaxDistance;
        this.gpuCellSize = this.occupancy3d?.cellSizeM || 0.04;
        if (this.occupancy3d) delete this.occupancy3d.data;
        this.dispatchEvent({ type: 'changed', surface: this });
      })
      .catch((err) => {
        this.error = err;
        const msg = surfaceI18n(
          'surfaces.occupancy_worker_missing',
          'Surface occupancy worker failed to load.'
        );
        viewer?.postError?.(msg);
      });
  }

  /** Unit world-space plane normal, or null before the first valid plane. */
  worldNormal(target = _spinDir) {
    if (!this.hasPlane && !this.detected) return null;
    const n = this.plane?.normal;
    if (!Array.isArray(n) || n.length < 3) return null;
    const sign = this.flipped ? -1 : 1;
    target.set(n[0] * sign, n[1] * sign, n[2] * sign);
    if (!Number.isFinite(target.x) || !Number.isFinite(target.y) || !Number.isFinite(target.z) || target.lengthSq() < 1e-12) {
      return null;
    }
    return target.normalize();
  }

  _updateArrow() {
    this.seedSphere.material.color.copy(this.color);
    this.spinner.material.color.copy(this.color);
    const dir = this.worldNormal();
    if (!dir) {
      this.arrow.visible = false;
      return;
    }
    this.arrow.visible = true;
    this.arrow.setDirection(dir);
    this.arrow.position.copy(this.seedSphere.position);
    this.arrow.setColor(this.color);
  }

  _updateOverlay(viewer) {
    if (!this.pointcloud) {
      this.overlay.visible = false;
      return;
    }
    const scenePos = offsetFrameToScene(this.pointcloud, this.seedOffset);
    this.seedSphere.position.copy(scenePos);
    this._updateArrow();
    this._updateExtremes();
    this.overlay.visible = this.visible;
    void viewer;
  }

  /** Effective range below the zero level (pinned, else the grown slab, else the draft distance). */
  profileRangeBelowEffectiveM() {
    return effectiveProfileRangeM(this.profileRangeBelowM, this.grownMaxDistance, this.maxDistance);
  }

  /** Effective range above the zero level (pinned, else the grown slab, else the draft distance). */
  profileRangeAboveEffectiveM() {
    return effectiveProfileRangeM(this.profileRangeAboveM, this.grownMaxDistance, this.maxDistance);
  }

  /** The height-profile parameters the volume / extremes are computed for. */
  _profileParams() {
    return {
      profileRef: this.profileRef,
      flipped: !!this.flipped,
      profileOffsetM: this.profileOffsetM,
      rangeBelowM: this.profileRangeBelowEffectiveM(),
      rangeAboveM: this.profileRangeAboveEffectiveM(),
      volumeTop: this.volumeTop,
    };
  }

  /**
   * Recompute volume + extremes after `Surface.volumeDebounceMs` of quiet —
   * sliders call this on every tick; only the last one runs.
   */
  scheduleVolume() {
    if (this._volumeTimer) clearTimeout(this._volumeTimer);
    this._volumeTimer = setTimeout(() => {
      this._volumeTimer = null;
      this.computeVolume();
    }, Surface.volumeDebounceMs);
  }

  /**
   * Integrate the plaster volume for the current profile (worker when
   * available). A newer call or a dispose makes an in-flight result stale.
   */
  computeVolume() {
    if (this._volumeTimer) {
      clearTimeout(this._volumeTimer);
      this._volumeTimer = null;
    }
    const epoch = ++this._volumeEpoch;
    if (!this.heightRaster || !this.hasPlane) {
      this.volumeM3 = null;
      this.volumeErrorM3 = null;
      this.profileAreaM2 = null;
      this.highlightExtremes = null;
      this._updateExtremes();
      return Promise.resolve();
    }
    return profileVolumeAsync(this.heightRaster, this.plane, this.centroid, this._profileParams()).then(
      (r) => {
        if (epoch !== this._volumeEpoch) return;
        // `null` = the integral failed: unknown, not stale.
        this.volumeM3 = r ? r.volumeM3 : null;
        this.profileAreaM2 = r ? r.areaM2 : null;
        // Same in-plane offset as area: a half-cell silhouette shift changes
        // plaster volume by mean thickness × ΔA.
        this.volumeErrorM3 = !r ? null : r.areaM2 > 0 ? this.areaErrorM2 * (r.volumeM3 / r.areaM2) : 0;
        this.highlightExtremes = r ? r.extremes : null;
        this._updateExtremes();
        this.dispatchEvent({ type: 'changed', surface: this });
      }
    );
  }

  /** Unit profile normal on the side the extremes were computed for, or null before a plane exists. */
  _extremesNormal(ext, target) {
    const n = this.plane?.normal;
    if (!Array.isArray(n) || n.length < 3) return null;
    const pn = profileNormal(this.profileRef, n);
    const sign = ext.flipped ? -1 : 1;
    target.set(pn[0] * sign, pn[1] * sign, pn[2] * sign);
    return target.lengthSq() < 1e-12 ? null : target.normalize();
  }

  /**
   * Place the extreme-point markers: highest and lowest highlighted cell,
   * and the thickness line from the lowest up to the plaster top (the highest
   * point, or the zero level when the proud is chipped off) along the profile
   * normal. Labels are created on first show (TextSprite needs a canvas)
   * and placed by `updateScale`, which knows the camera distance.
   */
  _updateExtremes() {
    const ext = this.highlightExtremes;
    const show = !!(this.showHighlightExtremes && ext && this.pointcloud);
    this.extremes.visible = show;
    if (!show) return;
    _extentOffset.set(ext.high[0], ext.high[1], ext.high[2]);
    this.highMarker.position.copy(offsetFrameToScene(this.pointcloud, _extentOffset));
    _extentOffset.set(ext.low[0], ext.low[1], ext.low[2]);
    this.lowMarker.position.copy(offsetFrameToScene(this.pointcloud, _extentOffset));
    this.highMarker.material.color.copy(this.color);
    this.lowMarker.material.color.copy(this.color);
    this.thicknessLine.material.color.copy(this.color);

    const thickness = ext.thicknessM;
    const n = this._extremesNormal(ext, _worldN);
    const pos = this.thicknessLine.geometry.getAttribute('position');
    _extentPos.copy(this.lowMarker.position);
    pos.setXYZ(0, _extentPos.x, _extentPos.y, _extentPos.z);
    if (n) _extentPos.addScaledVector(n, thickness);
    pos.setXYZ(1, _extentPos.x, _extentPos.y, _extentPos.z);
    pos.needsUpdate = true;
    // Frustum culling tests the cached sphere: recompute it or the line
    // vanishes once the extremes move away from where it was first drawn.
    this.thicknessLine.geometry.computeBoundingSphere();
    this.thicknessLine.visible = !!n && thickness > 0;

    if (!this.highLabel && typeof document !== 'undefined') {
      this.highLabel = createExtentLabel();
      this.lowLabel = createExtentLabel();
      this.extremes.add(this.highLabel, this.lowLabel);
    }
    if (this.highLabel) {
      // setText is a no-op for an unchanged string, so per-frame calls are cheap.
      this.highLabel.setText(
        `${surfaceI18n('surfaces.extreme_high', 'Highest')} ${formatExtentCm(ext.highHeightM)}`
      );
      // The thickness stays on the line and in the panel info, not on the label.
      this.lowLabel.setText(`${surfaceI18n('surfaces.extreme_low', 'Lowest')} ${formatExtentCm(ext.lowHeightM)}`);
    }
  }

  updateScale(camera, renderer) {
    const s = scaleMeasureMarker(this.seedSphere, camera, renderer);
    if (!s) return;
    const arrowLen = Math.max(0.4, s * 6);
    this._arrowLen = arrowLen;
    this.arrow.setLength(arrowLen, s * 1.5, s);
    this._updateSpinner(camera, s, arrowLen);
    if (this.extremes.visible) {
      // Each marker keeps a constant screen size from its own camera
      // distance, like measurement spheres — not the seed's distance, which
      // can be tens of metres off when the camera is close to one end.
      const highScale = scaleMeasureMarker(this.highMarker, camera, renderer) ?? s;
      const lowScale = scaleMeasureMarker(this.lowMarker, camera, renderer) ?? s;
      if (this.highLabel) {
        scaleMeasureMarker(this.highLabel, camera, renderer, 70);
        scaleMeasureMarker(this.lowLabel, camera, renderer, 70);
        // Float the labels just above / below their markers.
        this.highLabel.position.copy(this.highMarker.position).addScaledVector(_spinZ, highScale * 0.8);
        this.lowLabel.position.copy(this.lowMarker.position).addScaledVector(_spinZ, -lowScale * 0.8);
      }
    }
  }

  _updateSpinner(camera, markerScale, arrowLen) {
    const show = !!(this.busy && this.visible);
    this.spinner.visible = show;
    if (!show) return;
    this.spinner.position.copy(this.seedSphere.position);
    const n = this.worldNormal(_worldN);
    if (n) {
      this.spinner.quaternion.setFromUnitVectors(_spinZ, n);
      this.spinner.position.addScaledVector(n, Math.max(markerScale * 0.35, arrowLen * 0.12));
    } else if (camera) {
      camera.getWorldDirection(_spinCam);
      _spinCam.negate();
      if (_spinCam.lengthSq() > 1e-12) {
        this.spinner.quaternion.setFromUnitVectors(_spinZ, _spinCam.normalize());
      }
    }
    this._spin = (this._spin + 0.14) % (Math.PI * 2);
    this.spinner.rotateZ(this._spin);
    this.spinner.scale.setScalar(markerScale * 2.6);
  }

  setVisible(v) {
    this.visible = v;
    this.overlay.visible = v;
    this.dispatchEvent({ type: 'changed', surface: this });
  }

  /**
   * Lock / unlock the two profile ranges. Locking snaps the range above to
   * the range below (a null "follow" stays null on both) so the sliders agree
   * from that moment; unlocking changes nothing until a slider moves.
   */
  setProfileRangeLinked(value) {
    const next = value !== false;
    if (next === this.profileRangeLinked) return;
    this.profileRangeLinked = next;
    const snapped = next && this.profileRangeAboveM !== this.profileRangeBelowM;
    if (snapped) this.profileRangeAboveM = this.profileRangeBelowM;
    this.dispatchEvent({ type: 'changed', surface: this });
    if (snapped) this.scheduleVolume();
  }

  setShowHighlightExtremes(value) {
    const next = !!value;
    if (next === this.showHighlightExtremes) return;
    this.showHighlightExtremes = next;
    this._updateExtremes();
    this.dispatchEvent({ type: 'changed', surface: this });
  }

  /**
   * Flip the plaster side. Membership, plane and raster do not depend on it,
   * so this is a profile change: the volume is recomputed here, no grow.
   */
  setFlipped(value) {
    const next = !!value;
    if (next === this.flipped) return;
    this.flipped = next;
    this._updateArrow();
    this.dispatchEvent({ type: 'changed', surface: this });
    this.scheduleVolume();
  }

  setVisMode(mode) {
    const next = normalizeSurfaceVis({ visMode: mode });
    if (next === this.visMode) return;
    this.visMode = next;
    this.dispatchEvent({ type: 'changed', surface: this });
  }

  setProfileRef(value) {
    const next = normalizeProfileRef(value);
    if (next === this.profileRef) return;
    this.profileRef = next;
    this.dispatchEvent({ type: 'changed', surface: this });
    this.scheduleVolume();
  }

  /** Volume definition only — no shader change, no grow; the integral is redone. */
  setVolumeTop(value) {
    const next = normalizeVolumeTop(value);
    if (next === this.volumeTop) return;
    this.volumeTop = next;
    this.dispatchEvent({ type: 'changed', surface: this });
    this.scheduleVolume();
  }

  setProfileBands(value) {
    const next = normalizeProfileBands(value);
    if (next === this.profileBands) return;
    this.profileBands = next;
    this.dispatchEvent({ type: 'changed', surface: this });
  }

  get rainbow() {
    return this.visMode === SURFACE_VIS.RAINBOW;
  }

  dispose() {
    this._occEpoch += 1;
    this._volumeEpoch += 1;
    if (this._volumeTimer) {
      clearTimeout(this._volumeTimer);
      this._volumeTimer = null;
    }
    this.occTexture?.dispose?.();
    this.occTexture = null;
    this.seedSphere.material.dispose();
    this.spinner.material.dispose();
    this.highMarker.material.dispose();
    this.lowMarker.material.dispose();
    this.thicknessLine.geometry.dispose();
    this.thicknessLine.material.dispose();
    for (const label of [this.highLabel, this.lowLabel]) {
      label?.material?.dispose?.();
      label?.texture?.dispose?.();
    }
  }
}
